/* Skallet rundt LOS Marine Workspace som statisk replika: modulrail, Arbeid-panelet, mobiltopp og toasts.
   Samme mål og farger som appen (rail 64 px, panel 234 px, Inter/Poppins, navy/LOS-blå), men uten data-henting. */
import { Building2, CalendarRange, Check, Home, Inbox, Kanban, LayoutPanelLeft, MessagesSquare, Package, Plus, Search, Settings, SquarePen } from "lucide-react";
import { LosMark } from "@/components/Logo";
import { CASE, USER, WORK } from "./losMockData";

export const Avatar = ({ initials = USER.initials, color = "#003867", size = 34, className = "" }) => (
  <span className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ring-1 ring-black/[0.06] ${className}`} style={{ width: size, height: size, background: color, fontSize: Math.round(size * 0.36) }}>
    {initials}
  </span>
);

/* kundens «logo»: en rund-kantet flis med initialer – slik appen viser kunder uten logo */
export const CoMark = ({ initials, color = "#003867", size = 30, className = "" }) => (
  <span className={`inline-flex shrink-0 items-center justify-center rounded-[8px] font-semibold text-white ${className}`} style={{ width: size, height: size, background: color, fontSize: Math.round(size * 0.4) }}>
    {initials}
  </span>
);

export const MODULES = [
  ["hjem", Home],
  ["arbeid", MessagesSquare],
  ["innboks", Inbox, 1],
  ["pipeline", Kanban],
  ["plan", CalendarRange],
  ["kunder", Building2],
  ["produkter", Package],
];

export const Rail = ({ active }) => (
  <nav className="flex h-full w-16 shrink-0 flex-col items-center bg-[hsl(var(--sidebar))] py-2.5" aria-hidden="true">
    <span className="flex h-11 w-11 items-center justify-center"><LosMark className="h-[30px] w-[30px]" /></span>
    <div className="mt-3 flex flex-col items-center gap-1">
      {MODULES.map(([k, Icon, badge]) => {
        const on = k === active;
        return (
          <span key={k} className={`relative flex h-11 w-11 items-center justify-center rounded-[12px] ${on ? "bg-white text-[hsl(var(--navy))] shadow-[0_1px_2px_hsl(var(--shadow-color)/0.1),0_0_0_1px_hsl(var(--shadow-color)/0.07)]" : "text-foreground/60"}`}>
            <Icon className="h-[22px] w-[22px]" strokeWidth={on ? 2.1 : 1.8} />
            {badge ? <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 font-mono text-[10px] font-semibold text-white ring-2 ring-[hsl(var(--sidebar))]">{badge}</span> : null}
          </span>
        );
      })}
    </div>
    <div className="mt-auto flex flex-col items-center gap-1.5">
      <span className="flex h-11 w-11 items-center justify-center text-foreground/60"><Settings className="h-[22px] w-[22px]" strokeWidth={1.8} /></span>
      <span className="flex h-11 w-11 items-center justify-center"><Avatar /></span>
    </div>
  </nav>
);

const PanelRow = ({ r, on }) => (
  <div className={`flex items-start gap-2.5 rounded-[10px] px-2 py-2 ${on ? "bg-white shadow-[0_1px_2px_hsl(var(--shadow-color)/0.08),0_0_0_1px_hsl(var(--shadow-color)/0.06)]" : ""}`}>
    <CoMark initials={r.initials} color={r.color} size={28} className="mt-0.5" />
    <div className="min-w-0 flex-1 leading-tight">
      <div className="truncate text-[13px] font-medium text-foreground">{r.name}</div>
      <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">{r.customer} · {r.status}</div>
    </div>
    {r.warn ? <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--status-warn))]" /> : null}
  </div>
);

/* Arbeid-panelet til venstre for en sak: Nytt arbeid, så sakene gruppert slik appen gjør det */
export const WorkPanel = ({ activeName = CASE.name }) => (
  <aside className="flex h-full w-[234px] shrink-0 flex-col border-r border-black/[0.06] bg-[hsl(var(--sidebar))]" aria-hidden="true">
    <div className="flex h-12 items-center justify-between px-3.5">
      <span className="text-[15px] font-semibold text-foreground">Arbeid</span>
      <span className="flex items-center gap-1 text-foreground/50"><LayoutPanelLeft className="h-4 w-4" strokeWidth={1.8} /><Search className="h-4 w-4" strokeWidth={1.8} /></span>
    </div>
    <div className="px-2.5">
      <div className="flex items-center gap-2.5 rounded-[10px] px-2 py-2 text-[13px] font-medium text-foreground"><SquarePen className="h-4 w-4 text-foreground/60" strokeWidth={1.8} /> Nytt arbeid</div>
    </div>
    <div className="mt-2 px-4 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Saker</div>
    <div className="mb-noscroll mt-1 flex-1 space-y-3 overflow-hidden px-2.5 pb-3">
      {WORK.map((g) => (
        <div key={g.group}>
          <div className="flex items-center justify-between px-2 py-1 text-[11px] text-muted-foreground"><span>{g.group}</span><span className="tabular-nums">{g.rows.length}</span></div>
          <div className="space-y-0.5">{g.rows.map((r) => <PanelRow key={r.name} r={r} on={r.name === activeName} />)}</div>
        </div>
      ))}
    </div>
  </aside>
);

/* mobiltoppen: merke · tittel · søk · avatar */
export const MobileTop = ({ title }) => (
  <header className="flex h-12 shrink-0 items-center justify-between border-b border-black/[0.06] bg-white px-3" aria-hidden="true">
    <span className="flex items-center gap-2.5"><LosMark className="h-7 w-7" /><span className="text-[15px] font-semibold text-foreground">{title}</span></span>
    <span className="flex items-center gap-2 text-foreground/60"><Search className="h-[18px] w-[18px]" strokeWidth={1.8} /><Avatar size={28} /></span>
  </header>
);

export const Toast = ({ t, nar }) => (
  <div key={t[0]} className={`mb-toast absolute z-20 flex items-start gap-3 rounded-xl border border-black/[0.08] bg-white p-3.5 shadow-[0_1px_2px_hsl(var(--shadow-color)/0.06),0_24px_60px_-24px_hsl(var(--shadow-color)/0.4)] ${nar ? "inset-x-4 bottom-5" : "bottom-6 right-6 w-[380px]"}`} data-testid="los-toast">
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--status-ok)/0.12)] text-[hsl(var(--status-ok))]"><Check className="h-3 w-3" strokeWidth={2.6} /></span>
    <div className="min-w-0 text-[13px] leading-5"><div className="font-semibold text-foreground">{t[0]}</div><div className="text-muted-foreground">{t[1]}</div></div>
  </div>
);

export const Primary = ({ children, className = "", icon: Icon }) => (
  <span className={`inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full bg-[hsl(var(--navy))] px-4 text-[13px] font-semibold text-white shadow-[0_1px_2px_hsl(var(--shadow-color)/0.2),0_8px_18px_-10px_hsl(var(--shadow-color)/0.6)] ${className}`}>
    {Icon ? <Icon className="h-4 w-4" strokeWidth={2} /> : null}
    {children}
  </span>
);

export const Ghost = ({ children, className = "", icon: Icon }) => (
  <span className={`inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] font-medium text-foreground/80 ${className}`}>
    {Icon ? <Icon className="h-4 w-4" strokeWidth={1.9} /> : null}
    {children}
  </span>
);

export const NewWork = ({ nar }) => (
  <Primary icon={Plus}>{nar ? "Nytt" : "Nytt arbeid"}</Primary>
);

/* Skallet: rail · (panel) · hovedflate. full = rail + panel, compact = bare flaten, narrow = telefon med mobiltopp */
export const Shell = ({ mode = "full", active = "arbeid", panel = null, title, children, toast, testId, view, beat }) => {
  const nar = mode === "narrow";
  return (
    <div className="mb mb-cine ws flex h-full w-full overflow-hidden bg-background text-foreground" data-testid={testId} data-mode={mode} data-view={view} data-beat={beat}>
      {mode === "full" ? <Rail active={active} /> : null}
      {mode === "full" && panel ? panel : null}
      <div className="relative flex min-w-0 flex-1 flex-col bg-white">
        {nar ? <MobileTop title={title} /> : null}
        <div className="relative min-h-0 flex-1 overflow-hidden">{children}</div>
        {toast ? <Toast t={toast} nar={nar} /> : null}
      </div>
    </div>
  );
};
